import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { ApiError } from '../api/client';
import { useClassroom } from '../classroom/api';
import { EmptyState } from '../components/EmptyState';
import {
  useCreateWeek,
  useFillWeekFromDefault,
  useSubjects,
  useWeek,
  useWeekDayNoteMutation,
  useWeekDetailsMutation,
  useWeekFieldMutation,
  useWeekLessonMutation,
  useWeekPeriodMutations,
} from '../planner/api';
import { rememberWeek } from '../planner/lastWeek';
import { periodErrorText } from '../planner/periodError';
import { PlannerSheet } from '../planner/sheet/PlannerSheet';
import { SheetTitle } from '../planner/sheet/SheetTitle';
import { SubjectPalette } from '../planner/SubjectPalette';
import { currentWeek, dayPath, parseIsoWeek } from '../planner/weekMath';
import type { IsoWeek } from '../planner/weekMath';
import { logoUrl } from '../theme/applyTheme';
import { useTheme } from '../theme/api';

export default function WeekPage() {
  const target = parseIsoWeek(useParams());
  if (!target) {
    const now = currentWeek();
    return <Navigate to={`/week/${now.year}/${now.week}`} replace />;
  }
  // The key resets the sheet (open cell, picked day) when another week is opened.
  return <WeekView key={`${target.year}-${target.week}`} target={target} />;
}

/** The week sheet with its palette; the toolbar above carries navigation and the save status. */
export function WeekView({ target }: { target: IsoWeek }) {
  const { year, week } = target;
  const data = useWeek(year, week);
  const subjects = useSubjects();
  const create = useCreateWeek(year, week);
  const fillDefault = useFillWeekFromDefault(year, week);
  const saveCell = useWeekLessonMutation(year, week);
  const saveFields = useWeekFieldMutation(year, week);
  const saveDetails = useWeekDetailsMutation(year, week);
  const saveNote = useWeekDayNoteMutation(year, week);
  const periodMutations = useWeekPeriodMutations(year, week);
  const theme = useTheme().data;
  const logo = theme ? logoUrl(theme) : null;
  const classroom = useClassroom().data;
  useEffect(() => rememberWeek({ year, week }), [year, week]);

  const periodError = periodMutations.create.error ?? periodMutations.update.error ?? periodMutations.remove.error;
  const previousError =
    create.error instanceof ApiError && create.error.status === 404
      ? 'Er is geen eerdere week om te kopiëren.'
      : create.error
        ? 'De week kon niet worden aangemaakt. Probeer het opnieuw.'
        : null;

  if (data.isPending || subjects.isPending) return <p className="panel m-4 p-6">Laden…</p>;
  if (data.isError || subjects.isError) {
    return (
      <p role="alert" className="panel m-4 p-6">
        De week kon niet worden geladen. Vernieuw de pagina.
      </p>
    );
  }

  const title = <SheetTitle logoSrc={logo} name={classroom?.name ?? ''} />;

  if (data.data === null) {
    return (
      <main className="mx-auto my-4 w-full max-w-7xl px-3">
        <PlannerSheet periods={[]} subjects={subjects.data} lessons={[]} week={target} title={title}>
          <EmptyState
            title={`Week ${week} is nog leeg`}
            note="Begin vanuit je rooster, of neem de vorige week over."
            action={
              <div className="flex flex-wrap gap-3">
                <Button className="min-h-11" disabled={create.isPending} onClick={() => create.mutate('default')}>
                  Vul vanuit het rooster
                </Button>
                <Button variant="outline" className="min-h-11" disabled={create.isPending} onClick={() => create.mutate('previous')}>
                  Kopieer vorige week
                </Button>
                {previousError && (
                  <p role="alert" className="w-full text-sm">
                    {previousError}
                  </p>
                )}
              </div>
            }
          />
        </PlannerSheet>
      </main>
    );
  }

  const weekData = data.data;
  return (
    <main className="mx-auto my-4 flex w-full max-w-7xl gap-3 px-3">
      <div className="flex min-w-0 flex-1 flex-col">
        <PlannerSheet
          periods={weekData.periods}
          subjects={subjects.data}
          lessons={weekData.lessons}
          dayNotes={weekData.dayNotes ?? []}
          week={target}
          title={title}
          dayHref={(weekday) => dayPath(target, weekday)}
          onCellChange={(cell, value) => saveCell.mutateAsync({ ...cell, ...value }).catch(() => undefined)}
          onFieldsChange={(cell, values) => saveFields.mutate({ ...cell, values })}
          onDetailsChange={(cell, details) => saveDetails.mutate({ ...cell, ...details })}
          onDayNoteChange={(weekday, note) => saveNote.mutate({ weekday, note })}
          periodActions={{
            update: (id, input) => periodMutations.update.mutate({ id, ...input }),
            remove: (id) => periodMutations.remove.mutate(id),
            create: (input) => periodMutations.create.mutate(input),
            pending: periodMutations.create.isPending || periodMutations.update.isPending,
            error: periodError ? periodErrorText(periodError) : null,
          }}
        >
          {weekData.periods.length === 0 && (
            <EmptyState
              title="Nog geen tijdvakken"
              note="Deze week heeft nog geen tijdvakken, dus er is nog niets om in te vullen."
              action={
                <div className="flex flex-col items-start gap-2">
                  <Button className="min-h-11" disabled={fillDefault.isPending} onClick={() => fillDefault.mutate()}>
                    Tijdvakken uit het vaste rooster overnemen
                  </Button>
                  {fillDefault.isSuccess && (
                    <p role="status" className="text-sm">
                      Je rooster heeft ook nog geen tijdvakken. Stel ze in onder Tijdvakken.
                    </p>
                  )}
                  {fillDefault.isError && (
                    <p role="alert" className="text-sm">
                      Overnemen is niet gelukt. Probeer het opnieuw.
                    </p>
                  )}
                </div>
              }
            />
          )}
        </PlannerSheet>
      </div>
      <SubjectPalette subjects={subjects.data} />
    </main>
  );
}
